import { useLocation } from "wouter";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Form } from "@/components/ui/form/form";
import { TextInput } from "@/components/ui/form/text-input";
import { useModalError } from "@/hooks/use-modal-error";
import { Study } from "@/api/studio/studies/types/study.type";
import { useDeleteStudy } from "@/api/studio/studies/queries";

/* Prop Types */
interface DeleteStudyProps {
  projectId: string;
  study: Study;
}

/* Schema */
const deleteStudySchema = z.object({
  confirmation: z.string().refine((val) => val === "DELETE", {
    message: "You must enter DELETE to confirm",
  }),
});

/*
 * DeleteStudyModal component
 * This component is used to delete a study - we provide a confirmation input to ensure the user wants to delete the study
 */
export const DeleteStudyModal = ({ projectId, study }: DeleteStudyProps) => {
  /* Stores */
  const [, navigate] = useLocation();

  /* Queries */
  const { mutateAsync: deleteStudy, isPending: isDeleteStudyPending, isError: isDeleteStudyError } = useDeleteStudy();

  /* Hooks */
  const { close } = useModalError({ error: isDeleteStudyError });

  /* Handlers */
  const handleDelete = async () => {
    await deleteStudy({ projectId, studyId: study.id });
    navigate(`/projects/${projectId}`);
    close();
  };

  /* Render */
  return (
    <Form schema={deleteStudySchema} onSubmit={handleDelete}>
      {({ register, formState: { errors, isValid } }) => (
        <>
          {/* Header */}
          <div className="flex items-center justify-between pb-4">
            <h2 className="font-semibold">Delete study</h2>
            <XMarkIcon onClick={close} className="size-5 cursor-pointer" />
          </div>

          {/* Details */}
          <div className="pb-4">
            <p>Are you sure you want to delete this study? This action is immediate and cannot be undone. All media in the study will also be deleted.</p>
          </div>

          {/* Confirmation Input */}
          <TextInput
            name="confirmation"
            registration={register("confirmation")}
            label="Type DELETE to confirm"
            disabled={isDeleteStudyPending}
            error={errors.confirmation?.message}
            className="mb-4"
          />

          {/* Buttons */}
          <div className="flex justify-end pt-4">
            <Button type="submit" variant="error" disabled={isDeleteStudyPending || !isValid}>
              {isDeleteStudyPending ? "Deleting..." : "Delete study"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
};
